// AdCartContext.js - Context for managing advertisement cart
import React, { createContext, useContext, useReducer } from 'react';

const AdCartContext = createContext();

// Ad placement types and their details
export const AD_PLACEMENTS = {
  takeover: {
    id: 'takeover',
    name: 'Site-Wide Takeover',
    description: 'Hero Billboard + Premium placement across site',
    sizes: ['970×250'],
    monthlyPrice: 599,
    weeklyPrice: 199,
    note: 'Site‑wide, both slots'
  },
  sidebarPremium: {
    id: 'sidebarPremium',
    name: 'Sidebar Premium',
    description: 'Persistent right rail advertisement',
    sizes: ['300×600'],
    monthlyPrice: 299,
    weeklyPrice: 99
  },
  inContentMrec: {
    id: 'inContentMrec',
    name: 'In‑Content MREC',
    description: 'Embedded in article/show body',
    sizes: ['300×250'],
    monthlyPrice: 199,
    weeklyPrice: 75
  },
  sponsoredShowCard: {
    id: 'sponsoredShowCard',
    name: 'Sponsored Show Card',
    description: 'Highlighted card component',
    sizes: ['Native'],
    monthlyPrice: 149,
    weeklyPrice: 49
  },
  categoryTop: {
    id: 'categoryTop',
    name: 'Category Top',
    description: 'Pinned at category top in directory',
    sizes: ['Native'],
    monthlyPrice: 299,
    weeklyPrice: 99
  },
  featuredListing: {
    id: 'featuredListing',
    name: 'Featured Listing',
    description: 'Larger card with priority placement',
    sizes: ['Native'],
    monthlyPrice: 99,
    weeklyPrice: 49
  },
  standardListing: {
    id: 'standardListing',
    name: 'Standard Listing',
    description: 'Basic directory card placement',
    sizes: ['Native'],
    monthlyPrice: 49,
    weeklyPrice: 25
  },
  // Voice/Video/Audio Ad Types
  voiceAd15s: {
    id: 'voiceAd15s',
    name: 'Voice Ad - 15 seconds',
    description: 'Professional voice-over advertisement',
    sizes: ['Audio'],
    monthlyPrice: 199,
    weeklyPrice: 69,
    category: 'media',
    duration: '15s'
  },
  voiceAd30s: {
    id: 'voiceAd30s',
    name: 'Voice Ad - 30 seconds',
    description: 'Professional voice-over advertisement',
    sizes: ['Audio'],
    monthlyPrice: 299,
    weeklyPrice: 99,
    category: 'media',
    duration: '30s'
  },
  voiceAd60s: {
    id: 'voiceAd60s',
    name: 'Voice Ad - 60 seconds',
    description: 'Professional voice-over advertisement',
    sizes: ['Audio'],
    monthlyPrice: 499,
    weeklyPrice: 169,
    category: 'media',
    duration: '60s'
  },
  videoAd15s: {
    id: 'videoAd15s',
    name: 'Video Ad - 15 seconds',
    description: 'Professional video advertisement',
    sizes: ['Video'],
    monthlyPrice: 399,
    weeklyPrice: 139,
    category: 'media',
    duration: '15s'
  },
  videoAd30s: {
    id: 'videoAd30s',
    name: 'Video Ad - 30 seconds',
    description: 'Professional video advertisement',
    sizes: ['Video'],
    monthlyPrice: 599,
    weeklyPrice: 199,
    category: 'media',
    duration: '30s'
  },
  videoAd60s: {
    id: 'videoAd60s',
    name: 'Video Ad - 60 seconds',
    description: 'Professional video advertisement',
    sizes: ['Video'],
    monthlyPrice: 899,
    weeklyPrice: 299,
    category: 'media',
    duration: '60s'
  },
  animationAd15s: {
    id: 'animationAd15s',
    name: 'Animation Ad - 15 seconds',
    description: 'Custom animated advertisement',
    sizes: ['Animation'],
    monthlyPrice: 699,
    weeklyPrice: 239,
    category: 'media',
    duration: '15s'
  },
  animationAd30s: {
    id: 'animationAd30s',
    name: 'Animation Ad - 30 seconds',
    description: 'Custom animated advertisement',
    sizes: ['Animation'],
    monthlyPrice: 999,
    weeklyPrice: 339,
    category: 'media',
    duration: '30s'
  },
  animationAd60s: {
    id: 'animationAd60s',
    name: 'Animation Ad - 60 seconds',
    description: 'Custom animated advertisement',
    sizes: ['Animation'],
    monthlyPrice: 1499,
    weeklyPrice: 499,
    category: 'media',
    duration: '60s'
  },
  sideAd: {
    id: 'sideAd',
    name: 'Side Ad Integration',
    description: 'Integrated side advertisement during podcast',
    sizes: ['Audio Integration'],
    monthlyPrice: 799,
    weeklyPrice: 269,
    category: 'media',
    duration: 'Variable'
  }
};

// Initial state
const initialState = {
  items: [],
  billing: 'monthly', // 'monthly' or 'weekly'
  total: 0
};

// Action types
const ADD_TO_CART = 'ADD_TO_CART';
const REMOVE_FROM_CART = 'REMOVE_FROM_CART';
const UPDATE_QUANTITY = 'UPDATE_QUANTITY';
const SET_BILLING = 'SET_BILLING';
const CLEAR_CART = 'CLEAR_CART';

// Reducer
function adCartReducer(state, action) {
  switch (action.type) {
    case ADD_TO_CART: {
      const { placementId, billing } = action.payload;
      const placement = AD_PLACEMENTS[placementId];
      
      if (!placement) return state;

      const existingItem = state.items.find(item => item.id === placementId);
      
      let newItems;
      if (existingItem) {
        // Update quantity if item already exists
        newItems = state.items.map(item =>
          item.id === placementId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item
        const newItem = {
          id: placementId,
          name: placement.name,
          description: placement.description,
          sizes: placement.sizes,
          monthlyPrice: placement.monthlyPrice,
          weeklyPrice: placement.weeklyPrice,
          note: placement.note,
          quantity: 1,
          billing: billing || state.billing
        };
        newItems = [...state.items, newItem];
      }

      const newTotal = calculateTotal(newItems, state.billing);
      
      return {
        ...state,
        items: newItems,
        total: newTotal
      };
    }

    case REMOVE_FROM_CART: {
      const newItems = state.items.filter(item => item.id !== action.payload);
      const newTotal = calculateTotal(newItems, state.billing);
      
      return {
        ...state,
        items: newItems,
        total: newTotal
      };
    }

    case UPDATE_QUANTITY: {
      const { id, quantity } = action.payload;
      
      if (quantity <= 0) {
        return adCartReducer(state, { type: REMOVE_FROM_CART, payload: id });
      }

      const newItems = state.items.map(item =>
        item.id === id ? { ...item, quantity } : item
      );
      
      const newTotal = calculateTotal(newItems, state.billing);
      
      return {
        ...state,
        items: newItems,
        total: newTotal
      };
    }

    case SET_BILLING: {
      const newBilling = action.payload;
      const newTotal = calculateTotal(state.items, newBilling);
      
      return {
        ...state,
        billing: newBilling,
        total: newTotal
      };
    }

    case CLEAR_CART: {
      return initialState;
    }

    default:
      return state;
  }
}

// Helper function to calculate total
function calculateTotal(items, billing) {
  return items.reduce((total, item) => {
    const price = billing === 'monthly' ? item.monthlyPrice : item.weeklyPrice;
    return total + (price * item.quantity);
  }, 0);
}

// Context Provider
export function AdCartProvider({ children }) {
  const [state, dispatch] = useReducer(adCartReducer, initialState);

  const addToCart = (placementId, billing) => {
    dispatch({ type: ADD_TO_CART, payload: { placementId, billing } });
  };

  const removeFromCart = (placementId) => {
    dispatch({ type: REMOVE_FROM_CART, payload: placementId });
  };

  const updateQuantity = (placementId, quantity) => {
    dispatch({ type: UPDATE_QUANTITY, payload: { id: placementId, quantity } });
  };

  const setBilling = (billing) => {
    dispatch({ type: SET_BILLING, payload: billing });
  };

  const clearCart = () => {
    dispatch({ type: CLEAR_CART });
  };

  const getItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  const formatPrice = (amount) => {
    return `$${amount.toLocaleString()}`;
  };

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    setBilling,
    clearCart,
    getItemCount,
    formatPrice
  };

  return (
    <AdCartContext.Provider value={value}>
      {children}
    </AdCartContext.Provider>
  );
}

// Custom hook
export function useAdCart() {
  const context = useContext(AdCartContext);
  if (!context) {
    throw new Error('useAdCart must be used within an AdCartProvider');
  }
  return context;
}